import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { FOCUSABLE_SELECTOR } from '../../../../shared/utils/focusHelpers';
import StatusBadgeScroller from './StatusBadgeScroller';
import ExerciseOptionsMenu from './ExerciseOptionsMenu';
import ExerciseOptionPill from './ExerciseOptionPill';

/** The Options dropdown plus the row of status pills, as shown in the header
 * of every transposed table. Replaces the hand-rolled pill block each table
 * used to carry.
 *
 * `pillOptionIds` scopes which of the seven options this table surfaces - the
 * lift and tag tables show all of them, the SAQ+C and circuit tables only the
 * two completion ones, matching what they rendered before.
 *
 * Pills are filtered on `isChecked` alone, never on availability: a circuit
 * forces `completionOnly` on while the option itself is unavailable for
 * circuits, and that pill has always shown. Whether the coach can act on it is
 * a separate question, answered by `canToggle` - which is also what decides if
 * the pill gets an x. */
const Bar = styled('div')`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 5px;
`;

/** Below this, the footer can't give the pill scroller enough width to be worth
 * reading, so the pills come out and the row keeps the Options dropdown - a
 * fixed-width button that still fits, and the control a coach would reach for
 * to change the state the pills were reporting.
 *
 * Two thresholds because the pre-save footer also carries a SAVE button: at the
 * same footer width it has meaningfully less room left over than a saved one,
 * so it has to give the pills up sooner.
 *
 * Measured on the footer, whose width doesn't depend on what's in the bar; see
 * useIsNarrowerThan. Exported so the tables can't drift from them. */
export const PILLS_MIN_WIDTH_SAVED = 400;
export const PILLS_MIN_WIDTH_PRESAVE = 490;

/** Narrower still, and pre-save only: the row is superset picker + dropdown +
 * SAVE, all fixed-width on a nowrap line, so below this the dropdown has to go
 * too or the three of them overflow the footer. The saved tables have no SAVE
 * button and never get this tight, so they keep their dropdown at every width
 * and pass no `showOptionsMenu`. */
export const OPTIONS_MENU_MIN_WIDTH_PRESAVE = 320;

/** Move focus to the first control that follows the bar, without leaving the
 * item. Used when the removed pill was the last one in the row, so there is no
 * next x to land on. The house search from UpdateFutureOccurrencesModal,
 * anchored on the bar rather than on the removed button - that button is
 * already detached by the time this runs, and compareDocumentPosition on a
 * detached node reports DISCONNECTED with an unreliable FOLLOWING bit.
 *
 * Scoped to the item, not the document: the bar sits in the table footer, which
 * makes it the last focusable thing in a saved table. An unscoped search there
 * finds the *next exercise* and hands the coach's keyboard to it. The item is
 * the right unit - it holds all of one exercise's controls, and the table is
 * the last thing in it. Falls back to the document so the bar still works
 * rendered on its own. */
const focusAfterBar = (bar) => {
  const row = bar.closest('[data-exercise-item]');
  const candidates = Array.from((row || document).querySelectorAll(FOCUSABLE_SELECTOR))
    .filter((el) => !bar.contains(el)
      /** Not a tab stop, so not somewhere to land. FOCUSABLE_SELECTOR matches
       * `button:not([disabled])`, and the controls that opt out of tabbing here
       * - the scroll arrows, the sets stepper's +/- - use tabindex rather than
       * the disabled attribute, so they satisfy that clause. Mirrors the filter
       * in useTabCyclesWithinItem's getFocusablesIn. */
      && el.getAttribute('tabindex') !== '-1'
      // eslint-disable-next-line no-bitwise
      && (bar.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING));

  /** Focus and verify rather than trusting the first hit. FOCUSABLE_SELECTOR
   * does not filter hidden elements, and rc-dropdown portals its closed
   * overlays to the end of <body>, which "follows" everything on the page. A
   * hidden node silently refuses focus, so step on to the next candidate. */
  if (candidates.some((el) => {
    el.focus({ preventScroll: true });
    return document.activeElement === el;
  })) {
    return;
  }

  /** Nothing follows inside the item - the saved tables end at this bar. Wrap
   * to the table's entry field, which is where Tab off the last control already
   * lands (useTabCyclesWithinItem's focusEntryField, same anchor). */
  row?.querySelector('[data-focus-target]')?.focus({ preventScroll: true });
};

const ExerciseOptionsBar = ({
  options, pillOptionIds, showOptionsMenu, showPills,
}) => {
  const scopedOptions = options.filter((option) => pillOptionIds.includes(option.id));

  const barRef = useRef(null);
  const removedIdRef = useRef(null);
  const [focusRequest, setFocusRequest] = useState(0);

  /** Removing a pill unmounts the x that was just activated, which drops focus
   * to <body> and makes the coach's next Tab restart from the top of the
   * document. Hand focus forward instead: the next x still in the row, or the
   * first control after the bar if that was the last pill. */
  const handleRemove = (option) => () => {
    /** Only chase focus if this x actually held it. Safari does not focus a
     * button on click, so an unguarded move would pull the caret out of
     * whatever the coach was typing in; and the Options menu toggles these same
     * options while focus sits inside the menu, where it has to stay. */
    const active = document.activeElement;
    if (active && active.dataset && active.dataset.optionId === option.id) {
      removedIdRef.current = option.id;
      /** Batched into the same commit as the toggle below, so the effect that
       * reads it runs once, after the pill has gone. A counter rather than a
       * boolean so removing a second pill re-fires it. */
      setFocusRequest((n) => n + 1);
    }
    option.toggle();
  };

  useEffect(() => {
    if (!focusRequest || !barRef.current) return;
    const bar = barRef.current;
    const removedIndex = scopedOptions.findIndex((o) => o.id === removedIdRef.current);

    /** Read out of the DOM rather than predicted from the old pill list,
     * because the toggle itself changes which options are removable - clearing
     * Completion Only unlocks Bodyweight, which gains an x in this same commit. */
    const next = Array.from(bar.querySelectorAll('[data-option-id]'))
      .find((el) => scopedOptions.findIndex((o) => o.id === el.dataset.optionId) > removedIndex);

    /** preventScroll throughout: the pills sit in an overflow-x track inside a
     * table that scrolls too, and native focus scrolling walks every clipped
     * ancestor. */
    if (next) {
      next.focus({ preventScroll: true });
      return;
    }
    focusAfterBar(bar);
  }, [focusRequest]);

  return (
    /** Never returned as null, even with nothing in it: `flex: 1` on this
     * element is what holds SAVE against the right edge of the footer, and
     * dropping it would collapse the pre-save row onto the superset picker. */
    <Bar ref={barRef}>
      {showOptionsMenu && <ExerciseOptionsMenu options={scopedOptions} />}
      {showPills && (
      <StatusBadgeScroller>
        {scopedOptions
          .filter((option) => option.isChecked)
          .map((option) => (
            <ExerciseOptionPill
              key={option.id}
              optionId={option.id}
              label={option.label}
              onRemove={option.canToggle ? handleRemove(option) : null}
            />
          ))}
      </StatusBadgeScroller>
      )}
    </Bar>
  );
};

ExerciseOptionsBar.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    isChecked: PropTypes.bool,
    canToggle: PropTypes.bool,
    toggle: PropTypes.func,
  })),
  pillOptionIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  /** Hides the dropdown but keeps the pills. */
  showOptionsMenu: PropTypes.bool,
  /** Hides the pills but keeps the dropdown - the narrow-footer case. */
  showPills: PropTypes.bool,
};

ExerciseOptionsBar.defaultProps = {
  options: [],
  showOptionsMenu: true,
  showPills: true,
};

export default ExerciseOptionsBar;
